import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableItem from '../../../../../../components/Sortable/SortableItem';
import MedicationMedicine from '../../../../../../components/Users/OrderSetForm/components/MedicationMedicine';
import moveUpDownIcon from '../../../../../../../assets/img/medicine-profile-icons/move-up-down-icon.svg';
import type { Medication } from '../../../../../../../lib/transferObjects/Medication';
import {
  MedProfileStatus,
  MedProfileStatusEnum,
  MedSection,
} from '../../../../hook/types';
import MedicationDropZone from './MedicationDropZone';
import {
  getMedicationContainerId,
  getMedicationSortableId,
} from './medicationDragUtils';

type DeleteTarget = {
  medId: number;
  sectionName: string;
} | null;

type MedicationSortableListProps = {
  section: MedSection;
  sectionIndex: number;
  canEditMedicationProfile: boolean;
  canDragAndDrop: boolean;
  activeDragType: 'section' | 'medication' | null;
  activeDragId: string | null;
  activeMedicationSourceSectionName: string | null;
  profileStatus?: MedProfileStatus;
  deleteTarget: DeleteTarget;
  onRequestDelete: (medId: number, sectionName: string) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (medId?: number, sectionName?: string) => void;
  onEditMedicationToSection: (data: any, targetSection: string) => void;
  medicationProfileForm: any;
};

const MedicationSortableList = ({
  section,
  sectionIndex,
  canEditMedicationProfile,
  canDragAndDrop,
  activeDragType,
  activeDragId,
  activeMedicationSourceSectionName,
  profileStatus,
  deleteTarget,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onEditMedicationToSection,
  medicationProfileForm,
}: MedicationSortableListProps) => {
  const medications = Array.isArray(section.medications)
    ? section.medications
    : [];

  return (
    <MedicationDropZone
      id={getMedicationContainerId(section, sectionIndex)}
      disabled={!canDragAndDrop}
      sectionName={section.medSectionBreak}
      activeDragType={activeDragType}
      activeDragSourceSectionName={activeMedicationSourceSectionName}
    >
      <SortableContext
        items={medications.map(
          (medication: Partial<Medication>, medicationIndex: number) =>
            getMedicationSortableId(
              section,
              sectionIndex,
              medication,
              medicationIndex,
            ),
        )}
        strategy={verticalListSortingStrategy}
      >
        <div className="my-3 px-3 d-flex flex-column gap-2">
          {medications.map(
            (medication: Partial<Medication>, medicationIndex: number) => {
              const medicationSortableId = getMedicationSortableId(
                section,
                sectionIndex,
                medication,
                medicationIndex,
              );

              return (
                <SortableItem
                  key={`${medicationSortableId}${medication.medName ?? ''}`}
                  id={medicationSortableId}
                  disabled={!canDragAndDrop}
                  style={
                    activeDragType === 'medication' &&
                    activeDragId === medicationSortableId
                      ? { opacity: 0.2 }
                      : undefined
                  }
                  handle={
                    canDragAndDrop
                      ? ({ attributes, listeners, disabled }) => (
                          <button
                            type="button"
                            aria-label="Drag medication"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: '10px 0 0',
                              cursor: disabled ? 'default' : 'grab',
                            }}
                            {...attributes}
                            {...listeners}
                            disabled={disabled}
                          >
                            <img
                              src={moveUpDownIcon}
                              alt="drag sort"
                              style={{
                                width: 14,
                                height: 14,
                                opacity: disabled ? 0.5 : 1,
                                display: 'block',
                              }}
                            />
                          </button>
                        )
                      : undefined
                  }
                  contentStyle={{ width: '100%' }}
                >
                  <MedicationMedicine
                    currentSection={section.medSectionBreak}
                    onEditMedicationToSection={onEditMedicationToSection}
                    medication={medication}
                    canEdit={canEditMedicationProfile}
                    canViewOnly={
                      profileStatus === MedProfileStatusEnum.FINALISED ||
                      profileStatus === MedProfileStatusEnum.CONSUMER_CREATED
                    }
                    confirmDelete={!!profileStatus}
                    isDeleting={
                      deleteTarget?.medId === medication.medId &&
                      deleteTarget?.sectionName === section.medSectionBreak
                    }
                    onRequestDelete={() => {
                      if (typeof medication.medId !== 'number') return;
                      onRequestDelete(
                        medication.medId,
                        section.medSectionBreak,
                      );
                    }}
                    onCancelDelete={onCancelDelete}
                    onConfirmDelete={onConfirmDelete}
                    medicationProfileForm={medicationProfileForm}
                  />
                </SortableItem>
              );
            },
          )}
        </div>
      </SortableContext>
    </MedicationDropZone>
  );
};

export default MedicationSortableList;
